import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { MultiSelectionToggleGroup } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGroup';
import { EventStatusBanner } from 'src/components/Events/EventStatus';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Stack } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { ContentId } from 'src/utils/constants/contentId';
import { getInventorySummaryListingMetricsDisplayStrings } from 'src/utils/ticketMetricUtils';
import { EventWithData, Listing, ListingMetrics } from 'src/WebApiController';

import {
  EventHeaderMarketplacesWithMetrics,
  EventHeaderMetricsType,
} from '../../../../../Events/EventHeader/EventHeaderMarketplaceMetrics';
import { InventoryEventTabs } from '../../../InventoryEventTabs';
import { useInventoryEventLocalContext } from '../../InventoryEventLocalContext';
import {
  AnimatedEventHeaderContent,
  AnimatedEventInfo,
} from '../Animated/AnimatedEventHeader';
import * as styles from './InventoryEventHeaderMobile.css';

export const InventoryEventHeaderMobile = ({
  event,
  inventoryAccordionItem,
  returnUrl,
}: {
  event: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  returnUrl?: string;
}) => {
  const navigate = useNavigate();
  const { venue, performer } = useGetEventFullInfo(event.event);
  const { activeTab, marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useInventoryEventLocalContext();

  const { loginContext } = useAppContext();
  const { eventMetrics: eventMetricsMap } =
    useCatalogMetricsContext<ListingMetrics>();

  const formattedTotalMetrics = useMemo(() => {
    const eventMetrics = eventMetricsMap?.[event.event?.virtId ?? ''];
    const formattedTotalMetrics = eventMetrics
      ? getInventorySummaryListingMetricsDisplayStrings(
          eventMetrics,
          loginContext?.user?.activeAccount?.currencyCode
        )
      : undefined;
    return formattedTotalMetrics;
  }, [
    event.event?.virtId,
    eventMetricsMap,
    loginContext?.user?.activeAccount?.currencyCode,
  ]);

  const isInInventory = useMemo(() => {
    return [InventoryEventTabs.Info, InventoryEventTabs.ManualPricing].includes(
      activeTab
    );
  }, [activeTab]);

  return (
    <Stack gap={'s'} direction="column">
      <MobileHeaderBar
        onBack={() =>
          navigate(returnUrl ?? '/inventory', {
            state: {
              fromPosEventId: event.event?.virtId,
            },
          })
        }
        title={
          <>
            <AnimatedEventHeaderContent
              event={event.event}
              venue={venue}
              performer={performer}
            />
            {activeTab === InventoryEventTabs.Info && event.event?.virtId && (
              <MultiSelectionToggleGroup
                groupId={event.event?.virtId}
                enableHotkey={true}
                variant="button"
              />
            )}
          </>
        }
      />

      <Stack gap="m" direction="column">
        <EventStatusBanner status={event.event?.evStat!} />

        <AnimatedEventInfo
          event={event.event}
          venue={venue}
          performer={performer}
          inventoryAccordionItem={inventoryAccordionItem}
        />

        {!isInInventory && (
          <EventHeaderMarketplacesWithMetrics
            justifyContent="spaceBetween"
            className={styles.eventHeaderNavCard}
            event={event.event}
            venue={venue}
            marketplaceEventInfo={marketplaceEventInfo}
            onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
            metrics={{
              type: EventHeaderMetricsType.Summary,
              metrics: {
                label: ContentId.Listed,
                value: formattedTotalMetrics?.formattedTotalListPrice ?? 0,
              },
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};
